import { CheckOutlined, InfoCircleOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { filterByTokens } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Button, Flex, Input, Space, Table, Tag, Tooltip, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useDebounce } from 'use-debounce';
import { MainContent } from '../../../components/MainContent';
import { TableList } from '../../../components/TableList.tsx';
import { TestTubeColor } from '../../../components/TestTubeColor';
import type { SpecimensQuery } from '../../../graphql/generated/graphql.ts';
import { humanizedHours } from '../../../utils/dateFormatUtils';
import {
  testTubeColorProperties,
  translateSpecimenFeature,
  translateSpecimenGroup,
  translateTestTubeSupplier,
} from '../../../utils/enumHelpers';
import { SPECIMENS_QUERY } from './Specimens.gql';
import { CreateSpecimenModal } from './specimens/CreateSpecimenModal';
import { UpdateSpecimenModal } from './specimens/UpdateSpecimenModal';

const { useToken } = theme;

export type Specimen = NonNullable<SpecimensQuery['labSpecimens']>[number];
export type TestTube = NonNullable<SpecimensQuery['labTestTubes']>[number];
export type Form = NonNullable<SpecimensQuery['labForms']>[number];
export type AnalysisStation = NonNullable<SpecimensQuery['labAnalysisStations']>[number];

export const Specimens: React.FC = () => {
  const { token } = useToken();
  const { id: labId } = useParams<{ id: string }>();
  const [search, setSearch] = useState<string | null>(null);
  const [debouncedSearch] = useDebounce(search, 250);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editSpecimen, setEditSpecimen] = useState<Specimen | null>(null);

  const { data, loading, refetch } = useQuery(SPECIMENS_QUERY, {
    variables: {
      labId: labId ?? '',
    },
    pollInterval: 60000,
  });

  const columns: ColumnsType<Specimen> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
    {
      title: 'Materialkennung',
      dataIndex: 'classification',
      key: 'classification',
      ellipsis: true,
      sorter: (a, b) => a.classification.localeCompare(b.classification),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
    {
      title: 'Gruppe',
      dataIndex: 'group',
      key: 'group',
      ellipsis: true,
      sorter: (a, b) => a.group.localeCompare(b.group),
      render: value => translateSpecimenGroup(value),
    },
    {
      title: 'Lagerdauer (Stunden)',
      dataIndex: 'storagePeriod',
      key: 'storagePeriod',
      ellipsis: true,
      sorter: (a, b) => (a.storagePeriod === b.storagePeriod ? 0 : a.storagePeriod > b.storagePeriod ? 1 : -1),
      render: value => <Tooltip title={humanizedHours(value)}>{value}</Tooltip>,
    },
    {
      title: 'Lokalisationen',
      dataIndex: 'requiresLocalizations',
      key: 'requiresLocalizations',
      width: 140,
      sorter: (a, b) => Number(a.requiresLocalizations) - Number(b.requiresLocalizations),
      render: value => (value ? <CheckOutlined /> : ''),
    },
    {
      title: 'Probenbehälter',
      dataIndex: 'testTubes',
      key: 'testTubes',
      ellipsis: true,
      render: (_, record) => (
        <Space size="small">
          {record.testTubes.map(tt => (
            <Tooltip key={tt.id} title={tt.name}>
              <Tag>
                <Flex gap={4} align="center">
                  {translateTestTubeSupplier(tt.supplier)} <TestTubeColor properties={testTubeColorProperties(tt.color)} withText={false} />
                </Flex>
              </Tag>
            </Tooltip>
          ))}
        </Space>
      ),
    },
    {
      title: 'Eigenschaften',
      dataIndex: 'features',
      key: 'features',
      ellipsis: true,
      render: (_, record) => (
        <Space size="small">
          {record.features.map(f => (
            <Tag key={f}>{translateSpecimenFeature(f)}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Formulare',
      dataIndex: 'forms',
      key: 'forms',
      render: (_, record) => <TableList entries={record.forms.map(it => it.name)} maxEntries={3} />,
    },
    {
      title: 'Analysestationen',
      dataIndex: 'analysisStations',
      key: 'analysisStations',
      render: (_, record) => <TableList entries={record.analysisStations.map(it => it.name)} maxEntries={3} />,
    },
  ];

  const filteredSpecimens: Specimen[] = useMemo(
    () => filterByTokens(data?.labSpecimens ?? [], debouncedSearch, [s => s.name, s => s.classification]),
    [debouncedSearch, data]
  );

  return (
    <MainContent size="full">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: token.paddingSM }}>
        <Input
          allowClear
          autoFocus
          placeholder="Suche"
          value={search ?? ''}
          onChange={e => setSearch(e.target.value)}
          prefix={<SearchOutlined />}
          suffix={
            <Tooltip title="Suche nach Name oder Materialkennung">
              <InfoCircleOutlined />
            </Tooltip>
          }
          style={{ width: '300px', marginRight: token.paddingSM }}
        />
        <Button type="primary" icon={<PlusCircleOutlined />} disabled={loading} onClick={() => setCreateModalVisible(true)}>
          Neues Material
        </Button>
      </div>
      <Table<Specimen>
        scroll={{ x: 'max-content' }}
        rowKey={record => record.id}
        size="middle"
        showSorterTooltip={false}
        dataSource={filteredSpecimens}
        onRow={record => ({ onClick: () => setEditSpecimen(record), style: { cursor: 'pointer' } })}
        pagination={{
          showQuickJumper: true,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]} bis ${range[1]} von ${total} Materialien`,
        }}
        loading={loading}
        columns={columns}
      />
      <CreateSpecimenModal
        modalVisible={createModalVisible}
        onSuccess={() => refetch()}
        onClose={() => setCreateModalVisible(false)}
        labId={labId ?? ''}
        specimens={data?.labSpecimens ?? []}
        testTubes={data?.labTestTubes ?? []}
        forms={data?.labForms ?? []}
        analysisStations={data?.labAnalysisStations ?? []}
      />
      <UpdateSpecimenModal
        specimen={editSpecimen}
        onSuccess={() => refetch()}
        onClose={() => setEditSpecimen(null)}
        specimens={data?.labSpecimens ?? []}
        testTubes={data?.labTestTubes ?? []}
        forms={data?.labForms ?? []}
        analysisStations={data?.labAnalysisStations ?? []}
      />
    </MainContent>
  );
};
